import { APP_CONFIG }  from '@app/_app-config/_app-config_master';
import { APP_MODE }    from '@appConfig/app-mode-config';
import { BrandEnum }   from '@core/services/brand/brand.enums';
import { environment } from '@env/environment';
import {
	ApplicationScopeTypes,
	ArchivingScopesEnum,
	BusinessPremisesScopesEnum,
	ClientScopesEnum,
	ContractScopesEnum,
	EncryptionKeyScopesEnum,
	ErsExternalScopesEnum,
	ErsScopesEnum,
	FccVersionScopesEnum,
	FeatureScopesEnum,
	FiskalScopesEnum,
	KeywordScopesEnum,
	MyAccountScopesEnum,
	NotificationScopesEnum,
	OrganizationScopesEnum,
	OutageEventScopesEnum,
	StatisticsScopesEnum,
	StructureExportScopesEnum,
	StructureImportScopesEnum,
	SupportAdminScopesEnum,
	TaskScopesEnum,
	TaxationScopesEnum,
	TenantScopesEnum,
	TssExternalScopesEnum,
	TssScopesEnum,
	UsageArchivingScopesEnum,
	UsageDtrScopesEnum,
	UsageElsterScopesEnum,
	UsageFiskalScopesEnum,
	UsageGkScopesEnum,
	UserScopesEnum
}                      from '@models/scope.enums';
import { EnumUtils }   from '@utils/helpers';

export enum RoleClassification {
	ARCHIVING = 'archiving',
	ADDITIONAL = 'additional',
	MANAGEMENT = 'management',
	SUPPORT = 'support',
}

export interface IAdminAuthConfig {
	allowedRoutes: string[];
	defaultRoute: string;
	scopes: ApplicationScopeTypes[] | string[];
	type: RoleClassification;
}

// Admin Definitions ordered by Lower to Higher Permissions Roles

// MANAGEMENT
const adminSite: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.MANAGEMENT,
	scopes: [
		BusinessPremisesScopesEnum.ACCESS_CONFIGURATION,
		BusinessPremisesScopesEnum.ACCESS_FISKAL_CONTENT,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT_MODIFY,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT_MANUAL_EXPORT,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT_RESET_SCHEDULING,
		BusinessPremisesScopesEnum.DATA_INTEGRITY_ERRORS,
		BusinessPremisesScopesEnum.LIST,
		BusinessPremisesScopesEnum.READ,
		BusinessPremisesScopesEnum.REFRESH_ENTITY,
		BusinessPremisesScopesEnum.REFRESH_LIST,
		BusinessPremisesScopesEnum.REQUEST_RESOURCE_LOCK_LIST,
		BusinessPremisesScopesEnum.UPDATE,
		...EnumUtils.getEnumsValuesAsList( ErsScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( ErsExternalScopesEnum ),
		FeatureScopesEnum.ERIC,
		...EnumUtils.getEnumsValuesAsList( NotificationScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( OutageEventScopesEnum ),
		StructureExportScopesEnum.Download,
		StructureExportScopesEnum.ExportBusinessPremises,
		...EnumUtils.getEnumsValuesAsList( FiskalScopesEnum ),
		StructureExportScopesEnum.List,
		StructureExportScopesEnum.Read,
		StructureExportScopesEnum.RefreshEntity,
		StructureExportScopesEnum.RefreshList,
		TaskScopesEnum.ACCESS_TASK_CENTER,
		TaskScopesEnum.ACCESS_FCC_OPEN_TRANSACTIONS_TASK,
		TaskScopesEnum.ACCESS_FCC_VERSIONS_TASK,
		TaskScopesEnum.ACCESS_FISKAL_TSS_LOGS_TASK,
		TaskScopesEnum.ACCESS_NOTIFICATIONS_TASK,
		TaskScopesEnum.ACCESS_OUTAGES_TASK,
		TaxationScopesEnum.INDICATOR_SHOW,
		TaxationScopesEnum.REPORTING_CREATE,
		TaxationScopesEnum.REPORTING_DOWNLOAD,
		TaxationScopesEnum.REPORTING_LIST,
		TaxationScopesEnum.REPORTING_READ,
		TaxationScopesEnum.REPORTING_REFRESH_ENTITY,
		TaxationScopesEnum.REPORTING_REFRESH_LIST,
		TenantScopesEnum.ReadOwn,
		...EnumUtils.getEnumsValuesAsList( TssScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( TssExternalScopesEnum ),
		UsageFiskalScopesEnum.BUSINESS_PREMISES,
		UserScopesEnum.CHANGE_PASSWORD_CURRENT_USER,
		UserScopesEnum.UPDATE_CURRENT_USER
	],
	allowedRoutes: [
		`/${ APP_CONFIG.businesspremises.route }`,
		`/${ APP_CONFIG.currentUser.route }`,
		`/${ APP_CONFIG.ers.route }`,
		`/${ APP_CONFIG.structureExport.route }`,
		`/${ APP_CONFIG.taxations.route }`,
		`/${ APP_CONFIG.tenantConsole.route }`,
		`/${ APP_CONFIG.tss.route }`
	]
};

const adminEntity: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.MANAGEMENT,
	scopes: [
		...adminSite.scopes,
		BusinessPremisesScopesEnum.CREATE,
		BusinessPremisesScopesEnum.DELETE,
		BusinessPremisesScopesEnum.STATUS_DISABLE,
		BusinessPremisesScopesEnum.STATUS_ENABLE,
		EncryptionKeyScopesEnum.Create,
		EncryptionKeyScopesEnum.Download,
		EncryptionKeyScopesEnum.Renew,
		OrganizationScopesEnum.DATA_INTEGRITY_ERRORS,
		OrganizationScopesEnum.List,
		OrganizationScopesEnum.Read,
		OrganizationScopesEnum.RefreshEntity,
		OrganizationScopesEnum.RefreshList,
		OrganizationScopesEnum.Update,
		StructureExportScopesEnum.ExportOrganization,
		UsageFiskalScopesEnum.ORGANIZATION
	],
	allowedRoutes: [
		...adminSite.allowedRoutes,
		`/${ APP_CONFIG.organizations.route }`
	]
};

const adminTenant: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.MANAGEMENT,
	scopes: [
		BusinessPremisesScopesEnum.ACCESS_CONFIGURATION,
		BusinessPremisesScopesEnum.ACCESS_FISKAL_CONTENT,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT_MODIFY,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT_MANUAL_EXPORT,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT_RESET_SCHEDULING,
		BusinessPremisesScopesEnum.CREATE,
		BusinessPremisesScopesEnum.DATA_INTEGRITY_ERRORS,
		BusinessPremisesScopesEnum.DELETE,
		BusinessPremisesScopesEnum.LIST,
		BusinessPremisesScopesEnum.READ,
		BusinessPremisesScopesEnum.REFRESH_ENTITY,
		BusinessPremisesScopesEnum.REFRESH_LIST,
		BusinessPremisesScopesEnum.REQUEST_RESOURCE_LOCK_LIST,
		BusinessPremisesScopesEnum.STATUS_DISABLE,
		BusinessPremisesScopesEnum.STATUS_ENABLE,
		BusinessPremisesScopesEnum.UPDATE,
		...EnumUtils.getEnumsValuesAsList( ClientScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( EncryptionKeyScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( ErsScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( ErsExternalScopesEnum ),
		FeatureScopesEnum.ERIC,
		...EnumUtils.getEnumsValuesAsList( FiskalScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( NotificationScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( OrganizationScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( OutageEventScopesEnum ),
		StatisticsScopesEnum.ReadFiskalContent,
		...EnumUtils.getEnumsValuesAsList( StructureExportScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( StructureImportScopesEnum ),
		TaskScopesEnum.ACCESS_TASK_CENTER,
		TaskScopesEnum.ACCESS_DATA_INTEGRITY_ERRORS_TASK,
		TaskScopesEnum.ACCESS_FCC_OPEN_TRANSACTIONS_TASK,
		TaskScopesEnum.ACCESS_FCC_VERSIONS_TASK,
		TaskScopesEnum.ACCESS_FISKAL_TSS_LOGS_TASK,
		TaskScopesEnum.ACCESS_NOTIFICATIONS_TASK,
		TaskScopesEnum.ACCESS_OUTAGES_TASK,
		TaskScopesEnum.ACCESS_TSS_PRE_INITIALIZATION_TASK,
		TaxationScopesEnum.INDICATOR_SHOW,
		TaxationScopesEnum.REPORTING_CREATE,
		TaxationScopesEnum.REPORTING_DOWNLOAD,
		TaxationScopesEnum.REPORTING_LIST,
		TaxationScopesEnum.REPORTING_READ,
		TaxationScopesEnum.REPORTING_REFRESH_ENTITY,
		TaxationScopesEnum.REPORTING_REFRESH_LIST,
		TenantScopesEnum.TENANT_CONFIGURATION_ACCESS,
		TenantScopesEnum.CONFIGURATION_TENANT_ENCRYPTION_KEY,
		TenantScopesEnum.CONFIGURATION_TENANT_ENCRYPTION_KEY_MODIFY,
		TenantScopesEnum.CONFIGURATION_TENANT_TSS_EXPORT,
		TenantScopesEnum.CONFIGURATION_TENANT_TSS_EXPORT_MODIFY,
		TenantScopesEnum.CONFIGURATION_TENANT_TSS_EXPORT_RESET_SCHEDULING,
		TenantScopesEnum.CONFIGURATION_ACCOUNT_TSS_LOG_EXPIRATION_NOTIFICATION,
		TenantScopesEnum.CONFIGURATION_ACCOUNT_TSS_LOG_EXPIRATION_NOTIFICATION_MODIFY,
		TenantScopesEnum.ReadOwn,
		...EnumUtils.getEnumsValuesAsList( TssScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( TssExternalScopesEnum ),
		UsageFiskalScopesEnum.BUSINESS_PREMISES,
		UsageFiskalScopesEnum.ORGANIZATION,
		UsageFiskalScopesEnum.TENANT_CURRENT_USER,
		UserScopesEnum.CHANGE_PASSWORD,
		UserScopesEnum.CHANGE_PASSWORD_CURRENT_USER,
		UserScopesEnum.CREATE,
		UserScopesEnum.DELETE,
		UserScopesEnum.LIST,
		UserScopesEnum.READ,
		UserScopesEnum.REFRESH_ENTITY,
		UserScopesEnum.REFRESH_LIST,
		UserScopesEnum.STATUS_DISABLE,
		UserScopesEnum.STATUS_ENABLE,
		UserScopesEnum.UPDATE,
		UserScopesEnum.UPDATE_CURRENT_USER,
		UserScopesEnum.USER_AUDIT_USER
	],
	allowedRoutes: [
		...adminEntity.allowedRoutes,
		`/${ APP_CONFIG.configuration.route }`,
		`/${ APP_CONFIG.import.route }`,
		`/${ APP_CONFIG.users.route }`
	]
};

const adminAccount: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.MANAGEMENT,
	scopes: [
		...adminTenant.scopes,
		TenantScopesEnum.List,
		TenantScopesEnum.ManageTenant,
		TenantScopesEnum.Read,
		TenantScopesEnum.RefreshEntity,
		TenantScopesEnum.RefreshList,
		UsageFiskalScopesEnum.TENANT_ACCOUNT
	],
	allowedRoutes: [
		...adminTenant.allowedRoutes,
		`/${ APP_CONFIG.accountTenant.route }`
	]
};

const adminPartner: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.MANAGEMENT,
	scopes: [
		...adminAccount.scopes,
		TenantScopesEnum.Create,
		TenantScopesEnum.StatusDisable,
		TenantScopesEnum.StatusEnable,
		TenantScopesEnum.Update
	],
	allowedRoutes: [
		...adminTenant.allowedRoutes,
		`/${ APP_CONFIG.partnerTenant.route }`
	]
};

const adminVendor: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.MANAGEMENT,
	scopes: [
		...adminPartner.scopes.filter( scope =>
			!(
				[
					TenantScopesEnum.ManageTenant,
					TenantScopesEnum.StatusDisable,
					TenantScopesEnum.StatusEnable
				] as unknown as string
			).includes( scope )
		)
	],
	allowedRoutes: [
		...adminPartner.allowedRoutes
	]
};

const adminDistributor: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.MANAGEMENT,
	scopes: [
		...EnumUtils.getEnumsValuesAsList( NotificationScopesEnum ),
		TaskScopesEnum.ACCESS_NOTIFICATIONS_TASK,
		TenantScopesEnum.Create,
		TenantScopesEnum.List,
		TenantScopesEnum.Read,
		TenantScopesEnum.RefreshEntity,
		TenantScopesEnum.RefreshList,
		TenantScopesEnum.Update,
		StatisticsScopesEnum.ReadFiskalContent,
		UsageFiskalScopesEnum.TENANT_ACCOUNT,
		UsageFiskalScopesEnum.TENANT_CURRENT_USER,
		UserScopesEnum.CHANGE_PASSWORD,
		UserScopesEnum.CHANGE_PASSWORD_CURRENT_USER,
		UserScopesEnum.CREATE,
		UserScopesEnum.DELETE,
		UserScopesEnum.LIST,
		UserScopesEnum.READ,
		UserScopesEnum.REFRESH_ENTITY,
		UserScopesEnum.REFRESH_LIST,
		UserScopesEnum.STATUS_DISABLE,
		UserScopesEnum.STATUS_ENABLE,
		UserScopesEnum.UPDATE,
		UserScopesEnum.UPDATE_CURRENT_USER,
		UserScopesEnum.USER_AUDIT_USER
	],
	allowedRoutes: [
		`/${ APP_CONFIG.admin.route }`,
		`/${ APP_CONFIG.currentUser.route }`,
		`/${ APP_CONFIG.tenantConsole.route }`,
		`/${ APP_CONFIG.users.route }`
	]
};

const adminReseller: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.MANAGEMENT,
	scopes: [
		...adminPartner.scopes
	],
	allowedRoutes: [
		...adminPartner.allowedRoutes
	]
};


// ARCHIVING
const userArchiving: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.ARCHIVING,
	scopes: [
		...EnumUtils.getEnumsValuesAsList( ArchivingScopesEnum ),
		BusinessPremisesScopesEnum.ACCESS_ARCHIVING_CONTENT,
		BusinessPremisesScopesEnum.LIST,
		BusinessPremisesScopesEnum.READ,
		BusinessPremisesScopesEnum.REFRESH_ENTITY,
		BusinessPremisesScopesEnum.REFRESH_LIST,
		FeatureScopesEnum.ERIC,
		KeywordScopesEnum.List,
		KeywordScopesEnum.Read,
		...EnumUtils.getEnumsValuesAsList( NotificationScopesEnum ),
		StatisticsScopesEnum.ReadArchivingContent,
		TaskScopesEnum.ACCESS_NOTIFICATIONS_TASK,
		TaxationScopesEnum.INDICATOR_SHOW,
		UserScopesEnum.CHANGE_PASSWORD_CURRENT_USER,
		UserScopesEnum.UPDATE_CURRENT_USER
	],
	allowedRoutes: [
		`/${ APP_CONFIG.businesspremises.route }`,
		`/${ APP_CONFIG.currentUser.route }`,
		`/${ APP_CONFIG.tenantConsole.route }`
	]
};

const adminArchiving: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.ARCHIVING,
	scopes: [
		...userArchiving.scopes,
		BusinessPremisesScopesEnum.ACCESS_CONFIGURATION,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS_ARCHIVE,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS_MODIFY,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS_ARCHIVE_ALLOW_ENABLE,
		BusinessPremisesScopesEnum.CREATE,
		BusinessPremisesScopesEnum.UPDATE,
		KeywordScopesEnum.Create,
		KeywordScopesEnum.Delete,
		KeywordScopesEnum.Update,
		...EnumUtils.getEnumsValuesAsList( ClientScopesEnum ),
		OrganizationScopesEnum.Create,
		OrganizationScopesEnum.Delete,
		OrganizationScopesEnum.List,
		OrganizationScopesEnum.Read,
		OrganizationScopesEnum.RefreshEntity,
		OrganizationScopesEnum.RefreshList,
		OrganizationScopesEnum.StatusDisable,
		OrganizationScopesEnum.StatusEnable,
		OrganizationScopesEnum.StructureImport,
		OrganizationScopesEnum.Update,
		TenantScopesEnum.TENANT_CONFIGURATION_ACCESS,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_ALLOW_ENABLE,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_STRICT_REFERENCE_CHECK,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_STRICT_REFERENCE_CHECK_ALLOW_DISABLE,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_STRICT_REFERENCE_CHECK_ALLOW_ENABLE,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_ERS_CHECK,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_ERS_CHECK_ALLOW_DISABLE,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_ERS_CHECK_ALLOW_ENABLE,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_OPEN_ARCHIVE_MANAGE_KEYWORDS,
		UserScopesEnum.CHANGE_PASSWORD,
		UserScopesEnum.CHANGE_PASSWORD_CURRENT_USER,
		UserScopesEnum.CREATE,
		UserScopesEnum.DELETE,
		UserScopesEnum.LIST,
		UserScopesEnum.READ,
		UserScopesEnum.REFRESH_ENTITY,
		UserScopesEnum.REFRESH_LIST,
		UserScopesEnum.STATUS_DISABLE,
		UserScopesEnum.STATUS_ENABLE,
		UserScopesEnum.UPDATE,
		UserScopesEnum.UPDATE_CURRENT_USER,
		UserScopesEnum.USER_AUDIT_USER
	],
	allowedRoutes: [
		`/${ APP_CONFIG.businesspremises.route }`,
		`/${ APP_CONFIG.clients.route }`,
		`/${ APP_CONFIG.configuration.route }`,
		`/${ APP_CONFIG.currentUser.route }`,
		`/${ APP_CONFIG.organizations.route }`,
		`/${ APP_CONFIG.tenantConsole.route }`,
		`/${ APP_CONFIG.users.route }`
	]
};

// SUPPORT
const adminSupport: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.SUPPORT,
	scopes: [
		...EnumUtils.getEnumsValuesAsList( ArchivingScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( BusinessPremisesScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( ClientScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( ContractScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( EncryptionKeyScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( ErsScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( ErsExternalScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( FccVersionScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( FeatureScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( FiskalScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( KeywordScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( MyAccountScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( NotificationScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( OrganizationScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( OutageEventScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( StatisticsScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( StructureExportScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( StructureImportScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( SupportAdminScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( TaskScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( TaxationScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( TenantScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( TssScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( TssExternalScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( UsageArchivingScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( UsageElsterScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( UsageDtrScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( UsageFiskalScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( UsageGkScopesEnum ),
		...EnumUtils.getEnumsValuesAsList( UserScopesEnum )
	],
	allowedRoutes: [
		`/${ APP_CONFIG.admin.route }`,
		`/${ APP_CONFIG.adminConsole.route }`,
		`/${ APP_CONFIG.businesspremises.route }`,
		`/${ APP_CONFIG.clients.route }`,
		`/${ APP_CONFIG.configuration.route }`,
		`/${ APP_CONFIG.contracts.route }`,
		`/${ APP_CONFIG.currentUser.route }`,
		`/${ APP_CONFIG.ers.route }`,
		`/${ APP_CONFIG.import.route }`,
		`/${ APP_CONFIG.myAccount.route }`,
		`/${ APP_CONFIG.organizations.route }`,
		`/${ APP_CONFIG.structureExport.route }`,
		`/${ APP_CONFIG.taxations.route }`,
		`/${ APP_CONFIG.tenantConsole.route }`,
		`/${ APP_CONFIG.tss.route }`,
		`/${ APP_CONFIG.users.route }`
	]
};

// SUPPORT VIEW ("Support Admin Light")
const adminSupportView: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.SUPPORT,
	scopes: [
		ArchivingScopesEnum.ImportList,
		ArchivingScopesEnum.ImportRead,
		ArchivingScopesEnum.ImportRefreshEntity,
		ArchivingScopesEnum.ImportRefreshList,
		ArchivingScopesEnum.ImportCashPointClosingList,
		ArchivingScopesEnum.ImportCashPointClosingRead,
		ArchivingScopesEnum.ImportCashPointClosingRefreshEntity,
		ArchivingScopesEnum.ImportCashPointClosingRefreshList,
		ArchivingScopesEnum.ImportTssLogList,
		ArchivingScopesEnum.ImportTssLogRead,
		ArchivingScopesEnum.ImportTssLogRefreshEntity,
		ArchivingScopesEnum.ImportTssLogRefreshList,
		ArchivingScopesEnum.ExportList,
		ArchivingScopesEnum.ExportRead,
		ArchivingScopesEnum.ExportRefreshEntity,
		ArchivingScopesEnum.ExportRefreshList,
		ArchivingScopesEnum.ExportItemList,
		ArchivingScopesEnum.ExportItemRead,
		ArchivingScopesEnum.ExportItemRefreshEntity,
		ArchivingScopesEnum.ExportItemRefreshList,
		ArchivingScopesEnum.ExportTssLogList,
		ArchivingScopesEnum.ExportTssLogRead,
		ArchivingScopesEnum.ExportTssLogRefreshEntity,
		ArchivingScopesEnum.ExportTssLogRefreshList,
		ArchivingScopesEnum.ProceduralDocumentationList,
		ArchivingScopesEnum.ProceduralDocumentationRead,
		ArchivingScopesEnum.ProceduralDocumentationRefreshEntity,
		ArchivingScopesEnum.ProceduralDocumentationRefreshList,
		ArchivingScopesEnum.ProceduralDocumentList,
		ArchivingScopesEnum.ProceduralDocumentRead,
		ArchivingScopesEnum.ProceduralDocumentRefreshEntity,
		ArchivingScopesEnum.ProceduralDocumentRefreshList,
		ArchivingScopesEnum.ExportProceduralDocumentationList,
		ArchivingScopesEnum.ExportProceduralDocumentationRead,
		ArchivingScopesEnum.ExportProceduralDocumentationRefreshEntity,
		ArchivingScopesEnum.ExportProceduralDocumentationRefreshList,
		ArchivingScopesEnum.OpenArchiveList,
		ArchivingScopesEnum.OpenArchiveRead,
		ArchivingScopesEnum.OpenArchiveRefreshEntity,
		ArchivingScopesEnum.OpenArchiveRefreshList,
		ArchivingScopesEnum.OpenArchiveExportList,
		ArchivingScopesEnum.OpenArchiveExportRead,
		ArchivingScopesEnum.OpenArchiveExportRefreshEntity,
		ArchivingScopesEnum.OpenArchiveExportRefreshList,

		BusinessPremisesScopesEnum.ACCESS_ARCHIVING_CONTENT,
		BusinessPremisesScopesEnum.ACCESS_FISKAL_CONTENT,
		BusinessPremisesScopesEnum.ACCESS_CONFIGURATION,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS_ARCHIVE,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS_ELSTER,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT_RESET_SCHEDULING,
		BusinessPremisesScopesEnum.CONFIGURATION_TSS_EXPORT_MANUAL_EXPORT,
		BusinessPremisesScopesEnum.DATA_INTEGRITY_ERRORS,
		BusinessPremisesScopesEnum.LIST,
		BusinessPremisesScopesEnum.READ,
		BusinessPremisesScopesEnum.REFRESH_ENTITY,
		BusinessPremisesScopesEnum.REFRESH_LIST,

		ClientScopesEnum.List,
		ClientScopesEnum.Read,
		ClientScopesEnum.RefreshEntity,
		ClientScopesEnum.RefreshList,

		ContractScopesEnum.ACCESS_FCC_CONTENT,
		ContractScopesEnum.List,
		ContractScopesEnum.Read,
		ContractScopesEnum.RefreshEntity,
		ContractScopesEnum.RefreshList,

		EncryptionKeyScopesEnum.List,

		ErsScopesEnum.List,
		ErsScopesEnum.Read,
		ErsScopesEnum.RefreshEntity,
		ErsScopesEnum.RefreshList,

		ErsExternalScopesEnum.List,
		ErsExternalScopesEnum.Read,
		ErsExternalScopesEnum.RefreshEntity,
		ErsExternalScopesEnum.RefreshList,

		FccVersionScopesEnum.ACCESS_FCC_VERSION_CONTENTS,
		FccVersionScopesEnum.LIST,
		FccVersionScopesEnum.REFRESH_LIST,

		...EnumUtils.getEnumsValuesAsList( FeatureScopesEnum ),

		FiskalScopesEnum.TssLogList,
		FiskalScopesEnum.TssLogRead,
		FiskalScopesEnum.TssLogRefreshEntity,
		FiskalScopesEnum.TssLogRefreshList,

		KeywordScopesEnum.List,
		KeywordScopesEnum.Read,

		...EnumUtils.getEnumsValuesAsList( NotificationScopesEnum ),

		OrganizationScopesEnum.DATA_INTEGRITY_ERRORS,
		OrganizationScopesEnum.List,
		OrganizationScopesEnum.Read,
		OrganizationScopesEnum.RefreshEntity,
		OrganizationScopesEnum.RefreshList,

		OutageEventScopesEnum.AccessOutageEventContent,
		OutageEventScopesEnum.List,
		OutageEventScopesEnum.Read,
		OutageEventScopesEnum.RefreshEntity,
		OutageEventScopesEnum.RefreshList,

		StatisticsScopesEnum.ACCESS_GLOBAL_STATISTICS_CONTENTS,
		StatisticsScopesEnum.ReadArchivingContent,
		StatisticsScopesEnum.ReadFiskalContent,

		StructureExportScopesEnum.Download,
		StructureExportScopesEnum.List,
		StructureExportScopesEnum.Read,
		StructureExportScopesEnum.RefreshEntity,
		StructureExportScopesEnum.RefreshList,

		StructureImportScopesEnum.LIST,
		StructureImportScopesEnum.READ,
		StructureImportScopesEnum.REFRESH_ENTITY,
		StructureImportScopesEnum.DOWNLOAD_ENTITY_DATA,
		StructureImportScopesEnum.REFRESH_LIST,

		SupportAdminScopesEnum.IS_SUPPORT_ADMIN,

		TaskScopesEnum.ACCESS_TASK_CENTER,
		TaskScopesEnum.ACCESS_DATA_INTEGRITY_ERRORS_TASK,
		TaskScopesEnum.ACCESS_FCC_OPEN_TRANSACTIONS_TASK,
		TaskScopesEnum.ACCESS_FCC_VERSIONS_TASK,
		TaskScopesEnum.ACCESS_FISKAL_TSS_LOGS_TASK,
		TaskScopesEnum.ACCESS_MISSING_CUSTOMER_IDS_TASK,
		TaskScopesEnum.ACCESS_NOTIFICATIONS_TASK,
		TaskScopesEnum.ACCESS_OUTAGES_TASK,

		TaxationScopesEnum.INDICATOR_SHOW,
		TaxationScopesEnum.REPORTING_DOWNLOAD,
		TaxationScopesEnum.REPORTING_LIST,
		TaxationScopesEnum.REPORTING_READ,
		TaxationScopesEnum.REPORTING_REFRESH_ENTITY,
		TaxationScopesEnum.REPORTING_REFRESH_LIST,

		TenantScopesEnum.ACCESS_CONFIGURATION_ACCOUNT,
		TenantScopesEnum.TENANT_CONFIGURATION_ACCESS,
		TenantScopesEnum.CONFIGURATION_ACCOUNT_TSS_LOG_EXPIRATION_NOTIFICATION,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_STRICT_REFERENCE_CHECK,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_ERS_CHECK,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ARCHIVE_OPEN_ARCHIVE_MANAGE_KEYWORDS,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ELSTER,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ELSTER_ACCESS_ENABLE_TAXATION_REPORTING_EXPIRATION,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_ELSTER_ACCESS_TAXATION_REPORTING_DISABLE_EXPIRATION,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_FISKAL,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_FISKAL_ACCESS_DISABLE_TSS_LOG_EXPIRATION,
		TenantScopesEnum.CONFIGURATION_TENANT_ACCOUNT_SETTINGS_FISKAL_ACCESS_POSTPONED_REMOVAL_ACK_TSS_LOGS,
		TenantScopesEnum.CONFIGURATION_TENANT_ENCRYPTION_KEY,
		TenantScopesEnum.CONFIGURATION_TENANT_TSS_EXPORT,
		TenantScopesEnum.CONFIGURATION_TENANT_TSS_EXPORT_RESET_SCHEDULING,
		TenantScopesEnum.List,
		TenantScopesEnum.ManageTenant,
		TenantScopesEnum.ReadOwn,
		TenantScopesEnum.Read,
		TenantScopesEnum.RefreshEntity,
		TenantScopesEnum.RefreshList,

		TssScopesEnum.ACCESS_FCC_CONTENT,
		TssScopesEnum.CopyAll,
		TssScopesEnum.List,
		TssScopesEnum.PasswordRead,
		TssScopesEnum.Print,
		TssScopesEnum.Read,
		TssScopesEnum.RefreshEntity,
		TssScopesEnum.RefreshList,

		TssExternalScopesEnum.List,
		TssExternalScopesEnum.Read,
		TssExternalScopesEnum.RefreshEntity,
		TssExternalScopesEnum.RefreshList,

		UsageArchivingScopesEnum.BUSINESS_PREMISES,
		UsageArchivingScopesEnum.CONTRACT,
		UsageArchivingScopesEnum.ORGANIZATION,
		UsageArchivingScopesEnum.TENANT_ACCOUNT,
		UsageArchivingScopesEnum.TENANT_CURRENT_USER,

		UsageElsterScopesEnum.BUSINESS_PREMISES,
		UsageElsterScopesEnum.CONTRACT,
		UsageElsterScopesEnum.ORGANIZATION,
		UsageElsterScopesEnum.TENANT_ACCOUNT,
		UsageElsterScopesEnum.TENANT_CURRENT_USER,

		UsageDtrScopesEnum.ACCESS_DTR_CONTENTS,
		UsageDtrScopesEnum.ACCESS_DTR_USAGE_CONTENT,
		UsageDtrScopesEnum.ACCESS_DTR_VOLUME_CONTENT,

		UsageFiskalScopesEnum.BUSINESS_PREMISES,
		UsageFiskalScopesEnum.CONTRACT,
		UsageFiskalScopesEnum.ORGANIZATION,
		UsageFiskalScopesEnum.TENANT_ACCOUNT,
		UsageFiskalScopesEnum.TENANT_CURRENT_USER,

		UsageGkScopesEnum.ACCESS_GK_CONTENTS,
		UsageGkScopesEnum.ACCESS_GK_USAGE_CONTENT,

		UserScopesEnum.ACCESS_ALL_USERS_CONTENTS,
		UserScopesEnum.CHANGE_PASSWORD_CURRENT_USER,
		UserScopesEnum.LIST,
		UserScopesEnum.READ,
		UserScopesEnum.REFRESH_ENTITY,
		UserScopesEnum.REFRESH_LIST,
		UserScopesEnum.UPDATE_CURRENT_USER,
		UserScopesEnum.USER_AUDIT_ADMIN,
		UserScopesEnum.USER_AUDIT_USER
	],
	allowedRoutes: [
		...adminSupport?.allowedRoutes
	]
};

// CUSTOMER
const customerAdmin: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.myAccount.route }`,
	type: RoleClassification.ADDITIONAL,
	scopes: [
		ContractScopesEnum.ACCESS_FCC_CONTENT,
		ContractScopesEnum.List,
		ContractScopesEnum.Read,
		ContractScopesEnum.RefreshEntity,
		ContractScopesEnum.RefreshList,
		ContractScopesEnum.Update,
		...EnumUtils.getEnumsValuesAsList( MyAccountScopesEnum ),
		UsageArchivingScopesEnum.CONTRACT,
		UsageElsterScopesEnum.CONTRACT,
		UsageFiskalScopesEnum.CONTRACT,
	],
	allowedRoutes: [
		`/${ APP_CONFIG.myAccount.route }`
	]
};

const taxpayer: IAdminAuthConfig = {
	defaultRoute: `/${ APP_CONFIG.tenantConsole.route }`,
	type: RoleClassification.ADDITIONAL,
	scopes: [
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS_MODIFY,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS_ELSTER,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS_ELSTER_ENABLE_TAXATION_REPORTING_ALLOW_DISABLE,
		BusinessPremisesScopesEnum.CONFIGURATION_ACCOUNT_SETTINGS_ELSTER_ENABLE_TAXATION_REPORTING_ALLOW_DISABLE,
		FeatureScopesEnum.ERIC,
		...EnumUtils.getEnumsValuesAsList( TaxationScopesEnum )
	],
	allowedRoutes: [
		`/${ APP_CONFIG.taxations.route }`
	]
};

export const AUTH_CONFIG = {
	admin_support: adminSupport,
	admin_support_view: adminSupportView,
	admin_reseller: adminReseller,
	admin_partner: adminPartner,
	admin_vendor: adminVendor,
	admin_distributor: adminDistributor,
	admin_account: adminAccount,
	admin_tenant: adminTenant,
	admin_entity: adminEntity,
	admin_site: adminSite,
	admin_archiving: adminArchiving,
	customer_admin: customerAdmin,
	taxpayer: taxpayer,
	user_archiving: userArchiving
};

// if needed add/enable/disable here additional routes for development
if ( APP_CONFIG.appMode === APP_MODE.LOCAL ) {
	// AUTH_CONFIG.admin_tenant.allowedRoutes.unshift( `/${ APP_CONFIG.admin.route }` );
	AUTH_CONFIG.admin_support.allowedRoutes.push( `/${ APP_CONFIG.test.route }` );

	AUTH_CONFIG.admin_support_view.allowedRoutes.push( `/${ APP_CONFIG.test.route }` );

	AUTH_CONFIG.admin_tenant.allowedRoutes.push( `/${ APP_CONFIG.test.route }` );

	AUTH_CONFIG.admin_entity.allowedRoutes.push( `/${ APP_CONFIG.test.route }` );

	AUTH_CONFIG.admin_site.allowedRoutes.push( `/${ APP_CONFIG.test.route }` );

	AUTH_CONFIG.admin_archiving.allowedRoutes.push( `/${ APP_CONFIG.test.route }` );

	AUTH_CONFIG.user_archiving.allowedRoutes.push( `/${ APP_CONFIG.test.route }` );
}

// DF Brand
if ( environment.brandData.activeBranding === BrandEnum.DeutscheFiskal ) {

	// Feature Elster
	if ( !environment.features.elster.enabled ) {
		removeElsterFeature();
	}
}

function removeElsterFeature(): void {
	revokeElsterScopes();
	removeNotificationFeature();
	removeTaxationReportingRoute();
}


function removeNotificationFeature(): void {
	const revokeScopes: string[] = [
		TaskScopesEnum.ACCESS_NOTIFICATIONS_TASK as string
	];


	Object.values( AUTH_CONFIG ).forEach( ( adminConfig: IAdminAuthConfig ) => {
		const filteredScopes: ApplicationScopeTypes[] = [];

		adminConfig?.scopes.forEach( ( scope: ApplicationScopeTypes ) => {
			if ( !revokeScopes.includes( scope ) ) { filteredScopes.push( scope ); }
		} );

		adminConfig.scopes = filteredScopes;
	} );
}

function revokeElsterScopes(): void {
	const revokeScopes: string[] = [
		FeatureScopesEnum.ERIC as string,
		TaxationScopesEnum.INDICATOR_SHOW as string
	]
	// filter by exceptions
	.filter( ( scope: string ) =>
		'exceptedScopes' in environment.features.elster &&
		!environment.features.elster.exceptedScopes?.includes( scope as TaxationScopesEnum )
	);

	Object.values( AUTH_CONFIG ).forEach( ( adminConfig: IAdminAuthConfig ) => {
		const filteredScopes: ApplicationScopeTypes[] | string[] = adminConfig?.scopes.filter(
			( scope: ApplicationScopeTypes | string ) => !revokeScopes.includes( scope )
		);

		adminConfig.scopes = filteredScopes;
	} );
}

function removeTaxationReportingRoute(): void {
	const revokeRoutes: string[] = [
		`/${ APP_CONFIG.taxations.route }`
	];

	Object.values( AUTH_CONFIG ).forEach( ( adminConfig: IAdminAuthConfig ) => {
		const filteredRoutes: string[] = [];

		adminConfig?.allowedRoutes.forEach( ( allowedRoute: string ) => {
			if ( !revokeRoutes.includes( allowedRoute ) ) { filteredRoutes.push( allowedRoute ); }
		} );

		adminConfig.allowedRoutes = filteredRoutes;
	} );
}

// TODO either clean-up or create a proper feature flag
//  also: would make sense to have one `revokeScopes` function that is called
//  for every such feature, accepting an array of scopes
function removeFccDatacenterRelatedScopes(): void {
	const revokeScopes: string[] = [
		ContractScopesEnum.ACCESS_FCC_CONTENT as string,
		TssScopesEnum.ACCESS_FCC_CONTENT as string,
		TssScopesEnum.TRIGGER_FCC_DATACENTER_INSTALLATION as string
	];

	Object.values( AUTH_CONFIG ).forEach( ( adminConfig: IAdminAuthConfig ) => {
		const filteredScopes: ApplicationScopeTypes[] = [];

		adminConfig?.scopes.forEach( ( scope: ApplicationScopeTypes ) => {
			if ( !revokeScopes.includes( scope ) ) { filteredScopes.push( scope ); }
		} );

		adminConfig.scopes = filteredScopes;
	} );
}
